import React, { useEffect, useMemo, useState } from 'react';

import {
  ActionIcon,
  Button,
  Checkbox,
  Collapse,
  Group,
  Input,
  NumberInput,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
  createStyles,
  useMantineTheme,
} from '@mantine/core';
import { isInRange, isNotEmpty, useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { IconCaretRight, IconPencil, IconTrash } from '@tabler/icons-react';
import KiloAlert from 'components/KiloAlert';
import MoneyField from 'components/MoneyField';
import { useAuth } from 'context/auth-context';
import { useLocalization } from 'context/localization-context';
import useCreateBillingPlan from 'hooks/billingPlan/useCreateBillingPlan';
import useUpdateBillingPlan from 'hooks/billingPlan/useUpdateBillingPlan';
import { BillingPlanWithInstallments, PackageRetrieve } from 'types';
import {
  computeSessionValue,
  computeTotalEstimatedSessions,
  computeTotalValidity,
} from 'utils/billing.helpers';
import notificationHandler from 'utils/notifications';
import { v4 as uuidv4 } from 'uuid';

import ModalBillingPlanDelete from './components/ModalBillingPlanDelete';
import ModalBillingPlanEditConfirmation from './components/ModalBillingPlanEditConfirmation';
import RenewalBillingAlert from './components/RenewalBillingAlert';
import SessionValueDisplay from './components/SessionValueDisplay';
import { createSingleInstallment } from './installments';
import PackageDrawerState from '../../../../types';
import { PERIODOPTIONS } from '../../../PackageForm/types';

const useStyles = createStyles(theme => ({
  grid: {
    display: 'grid',
    gridTemplateColumns: '3fr 5fr',
    gap: theme.spacing.xs,
    justifyItems: 'stretch',
    alignItems: 'start',
    padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
  },
  installmentContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing.md,
    justifyItems: 'stretch',
    alignItems: 'start',
  },
}));

function validateTotalPrice(value: number | null, values: FormValues): string | null {
  const checkValue = Number((value || 0).toFixed(2));
  const sumOfInstallmentPrices = Number(
    values.installments.reduce((acc, cur) => acc + cur.price, 0).toFixed(2),
  );

  if (sumOfInstallmentPrices > checkValue)
    return `Total sum of installments is $${sumOfInstallmentPrices - checkValue} over total price`;

  if (sumOfInstallmentPrices < checkValue)
    return `Total sum of installments is $${checkValue - sumOfInstallmentPrices} under total price`;

  return null;
}

const createEquallyDividedInstallments = (totalPrice: number, numberOfInstallments: number): Array<any> => {
  const dividedPrice = Number((totalPrice / numberOfInstallments).toFixed(2));
  const bookKeeping = totalPrice - dividedPrice * numberOfInstallments;

  const installments = [];
  for (let i = 1; i <= numberOfInstallments; i++) {
    const installment = {
      installmentId: uuidv4(),
      installmentNumber: i,
      price: i === numberOfInstallments ? dividedPrice + bookKeeping : dividedPrice,
    };
    installments.push(installment);
  }
  return installments;
};

interface FormValues {
  billingPlanId: string;
  name: string;
  numberOfInstallments: number;
  installmentInterval: 'day' | 'week' | 'month' | 'year' | null;
  installmentIntervalCount: number | null;
  totalPrice: number;
  package: string;
  isEqualDivision: boolean;
  installments: Array<{
    installmentId: string;
    installmentNumber: number;
    price: number;
  }>;
}

interface IBillingPlanFormProps {
  state: PackageDrawerState;
  packageData: PackageRetrieve;
  billingPlan?: BillingPlanWithInstallments;
  isDisabled: boolean;
  permission?: boolean;
  handleCloseForm?: () => void;
}

const BillingPlanForm = ({
  state,
  packageData,
  billingPlan,
  isDisabled,
  permission = false,
  handleCloseForm = () => {},
}: IBillingPlanFormProps): JSX.Element => {
  const theme = useMantineTheme();
  const { classes } = useStyles();
  const { session } = useAuth();
  const { calculateInvoiceSummary, isTaxInclusive } = useLocalization();

  const [formState, setFormState] = useState<'create' | 'edit' | 'view'>(
    billingPlan === undefined ? 'create' : 'view',
  );
  const [isExpanded, { toggle, open, close }] = useDisclosure(true);
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);
  const [editConfirmationModalOpened, editConfirmationModalHandlers] = useDisclosure(false);

  const createBillingPlan = useCreateBillingPlan();
  const updateBillingPlan = useUpdateBillingPlan();

  const disableFields = isDisabled || formState === 'view';
  const numberOfInstallments = packageData.allowProration
    ? computeTotalValidity(packageData.attendanceRules)
    : 1;
  const billingPlanId = billingPlan ? billingPlan.billingPlanId : uuidv4();
  const formId = `billing-plan-form-${billingPlanId}`;

  const form = useForm<FormValues>({
    initialValues: billingPlan
      ? {
          ...billingPlan,
          isEqualDivision: billingPlan.isEqualPayments,
          totalPrice: billingPlan.totalPrice / 100,
          installments: billingPlan.installments.map(installment => {
            return {
              ...installment,
              installmentId: uuidv4(),
              price: installment.price / 100,
            };
          }),
        }
      : {
          billingPlanId: billingPlanId,
          name: '',
          numberOfInstallments: numberOfInstallments,
          installmentInterval: numberOfInstallments > 1 ? 'month' : null,
          //  By default it should be one whether 'proration' is one or not,
          //  and when 'proration' is on the field must be disabled for editing.
          installmentIntervalCount: 1,
          totalPrice: 0,
          package: packageData.packageId,
          isEqualDivision: true,
          installments: [...Array(numberOfInstallments)].map((_, i) => {
            return {
              installmentId: uuidv4(),
              installmentNumber: i + 1,
              price: 0,
            };
          }),
        },
    validate: {
      name: isNotEmpty('Form name cannot be empty'),
      numberOfInstallments: isInRange({ min: 1 }, 'Installments cannot be less than 1'),
      totalPrice: (value, values) => validateTotalPrice(value, values),
      installmentInterval: (value, values) =>
        values.numberOfInstallments > 1 && !packageData.allowProration && value == null
          ? 'Please select a billing frequency'
          : null,
      installmentIntervalCount: (value, values) =>
        values.numberOfInstallments > 1 && !packageData.allowProration && (value === null || value < 1)
          ? 'Please enter a valid billing frequency'
          : null,
    },
    transformValues: values => ({
      ...values,
      totalPrice: Math.round(values.totalPrice * 100),
      installments: values.installments.map(installment => {
        return {
          ...installment,
          price: Math.round(installment.price * 100),
        };
      }),
    }),
  });

  const hasMultipleInstallments = form.values.installments.length > 1;

  const totalEstimatedSessions = useMemo(
    () => computeTotalEstimatedSessions(packageData.attendanceRules, packageData.type),
    [packageData.attendanceRules, packageData.type],
  );

  const taxRate = packageData.taxRate?.rate ?? 0;

  const pretaxTotalPrice = useMemo(() => {
    if (isTaxInclusive && taxRate > 0) {
      const { subtotal } = calculateInvoiceSummary([
        { itemPrice: form.values.totalPrice, quantity: 1, taxRateValue: taxRate * 100 },
      ]);
      return subtotal;
    }

    return form.values.totalPrice;
  }, [form.values.totalPrice, isTaxInclusive, taxRate, calculateInvoiceSummary]);

  const sessionValue = useMemo(
    () => computeSessionValue(pretaxTotalPrice, totalEstimatedSessions),
    [pretaxTotalPrice, totalEstimatedSessions],
  );

  const handleCancelCreate = (): void => {
    handleCloseForm();
    form.reset();
  };

  const handleEdit = (): void => {
    setFormState('edit');
    open();
  };

  const handleCancelEdit = (): void => {
    setFormState('view');
    form.reset();
  };

  const handleTotalPriceChange = (newTotal: number): void => {
    if (form.values.isEqualDivision) {
      form.setFieldValue(
        'installments',
        createEquallyDividedInstallments(newTotal, form.values.numberOfInstallments),
      );
    }
    form.setFieldValue('totalPrice', newTotal);
  };

  const handleInstallmentNumberChange = (number: number): void => {
    if (number === 1) {
      form.setFieldValue('installmentInterval', null);
      form.setFieldValue('installmentIntervalCount', null);
      form.setFieldValue('installments', [
        createSingleInstallment(form.values.installments[0], form.values.totalPrice),
      ]);
    } else {
      if (form.values.installmentInterval === null) {
        form.setFieldValue('installmentInterval', packageData.attendanceRules[0].validityInterval);
      }
      if (form.values.installmentIntervalCount === null) {
        form.setFieldValue('installmentIntervalCount', numberOfInstallments);
      }
      if (form.values.isEqualDivision) {
        form.setFieldValue('installments', createEquallyDividedInstallments(form.values.totalPrice, number));
      } else {
        const installments = [];
        let totalPrice = 0;
        for (let i = 1; i <= number; i++) {
          if (typeof form.values.installments[i - 1] === 'undefined') {
            const installment = {
              installmentId: uuidv4(),
              installmentNumber: i,
              price: 0,
            };
            installments.push(installment);
          } else {
            totalPrice += form.values.installments[i - 1].price;
            installments.push(form.values.installments[i - 1]);
          }
        }
        form.setFieldValue('installments', installments);
        form.setFieldValue('totalPrice', totalPrice);
      }
    }
    form.setFieldValue('numberOfInstallments', number);
  };

  const handleEqualDivision = (event: React.FormEvent<HTMLInputElement>): void => {
    const isChecked = event.currentTarget.checked;
    if (isChecked) {
      form.setFieldValue(
        'installments',
        createEquallyDividedInstallments(form.values.totalPrice, form.values.numberOfInstallments),
      );
    }
    form.setFieldValue('isEqualDivision', isChecked);
  };

  const handleInstallmentPriceChange = (index: number, price: number | string): void => {
    if (typeof price === 'number') {
      let sum = 0;
      for (let i = 0; i < form.values.installments.length; i++) {
        sum += i === index ? price : form.values.installments[i].price;
      }
      form.setFieldValue(`installments.${index}.price`, price);
      form.setFieldValue('totalPrice', sum);
    }
  };

  const handleSubmit = (values: FormValues): void => {
    const { successNotification, errorNotification } = notificationHandler();

    if (
      formState === 'create' &&
      values.numberOfInstallments > 1 &&
      !packageData.allowProration &&
      !form.isDirty('installmentInterval')
    ) {
      form.setFieldError('installmentInterval', 'Please select a billing frequency');
      return;
    }

    const payload = {
      ...values,
      isEqualPayments: values.isEqualDivision,
      package: packageData.packageId,
      installments: values.installments.map(inst => ({
        installmentNumber: inst.installmentNumber,
        price: inst.price,
        percentageOfTotal: 0,
      })),
    };
    if (billingPlan) {
      updateBillingPlan.mutate(payload, {
        onError: error => {
          errorNotification('Billing plan update failed', error.response?.data.message);
        },
        onSuccess: () => {
          successNotification('Billing plan updated successfully');
          handleCloseForm();
          setFormState('view');
          editConfirmationModalHandlers.close();
          close();
        },
        onSettled: () => {},
      });
    } else {
      createBillingPlan.mutate(payload, {
        onError: error => {
          errorNotification('Billing plan creation failed', error.response?.data.message);
        },
        onSuccess: () => {
          successNotification('Billing plan created successfully');
          handleCloseForm();
          setFormState('view');
          close();
        },
        onSettled: () => {},
      });
    }
  };

  useEffect(() => {
    setFormState(billingPlan === undefined ? 'create' : 'view');
  }, [state, billingPlan]);

  return (
    <>
      <form id={formId} onSubmit={form.onSubmit(handleSubmit)}>
        <Paper shadow='sm' withBorder radius='lg' bg={isDisabled ? 'gray.1' : undefined}>
          <Group
            position='apart'
            noWrap
            bg={theme.colors.gray[2]}
            py='xs'
            px='sm'
            sx={{
              borderTopLeftRadius: theme.radius.lg,
              borderTopRightRadius: theme.radius.lg,
              borderBottomLeftRadius: !isExpanded ? theme.radius.lg : 0,
              borderBottomRightRadius: !isExpanded ? theme.radius.lg : 0,
              transition: 'ease-in 100ms',
            }}
          >
            <Group noWrap spacing='xs'>
              <IconCaretRight
                onClick={toggle}
                size={theme.fontSizes.xl}
                color='black'
                fill='black'
                style={{
                  transform: isExpanded ? 'rotate(90deg)' : 'unset',
                  transition: 'ease-in 100ms',
                }}
              />
              <Text weight='bold' size='sm'>
                {billingPlan ? billingPlan.name : 'Add Billing Plan'}
              </Text>
            </Group>

            {formState === 'create' && (
              <Group position='right' noWrap spacing='xs'>
                <Button color='red' size='xs' p='xs' onClick={handleCancelCreate}>
                  Cancel
                </Button>
                <Button
                  color='green'
                  size='xs'
                  p='xs'
                  type='submit'
                  disabled={isDisabled}
                  loading={createBillingPlan.isLoading}
                  data-cy='saveBillingPlanButton'
                >
                  Save
                </Button>
              </Group>
            )}
            {formState === 'edit' && (
              <Group position='right' noWrap spacing='xs'>
                <Button color='red' size='xs' p='xs' onClick={handleCancelEdit}>
                  Cancel
                </Button>
                <Button onClick={editConfirmationModalHandlers.open} color='green' size='xs' p='xs'>
                  Save
                </Button>
              </Group>
            )}
            {formState === 'view' && !isDisabled && (
              <Group position='right' noWrap spacing='xs'>
                <ActionIcon
                  variant='transparent'
                  color='kilo-blue'
                  onClick={handleEdit}
                  disabled={!permission}
                >
                  <IconPencil />
                </ActionIcon>
                <ActionIcon
                  variant='transparent'
                  color='red'
                  onClick={openDeleteModal}
                  disabled={!permission}
                >
                  <IconTrash />
                </ActionIcon>
              </Group>
            )}
          </Group>
          <Collapse in={isExpanded}>
            <div className={classes.grid}>
              <Input.Label ta='right' my='0.25rem'>
                Plan Name
              </Input.Label>
              <TextInput
                {...form.getInputProps('name')}
                data-cy='billingPlanName'
                size='xs'
                required
                placeholder='Enter plan name'
                disabled={disableFields}
              />
              <Input.Label ta='right' my='0.25rem'>
                Total Price
              </Input.Label>
              <MoneyField
                country={session?.gym?.country}
                data-cy='totalPriceBillingPlanInput'
                size='xs'
                disabled={disableFields || !form.values.isEqualDivision}
                required
                value={form.values.totalPrice}
                onChange={handleTotalPriceChange}
                error={form.errors.totalPrice}
              />

              <Input.Label ta='right' my='0.25rem'>
                # of Installments
              </Input.Label>
              <NumberInput
                data-cy='numberOfInstallmentsInput'
                min={1}
                size='xs'
                disabled={disableFields || packageData.allowProration}
                value={form.values.numberOfInstallments}
                onChange={handleInstallmentNumberChange}
              />
              {form.values.numberOfInstallments > 1 && (
                <>
                  <Input.Label ta='right' my='0.25rem'>
                    Frequency
                  </Input.Label>
                  <Group noWrap>
                    <NumberInput
                      data-cy='frequencyBillingInput'
                      min={1}
                      size='xs'
                      disabled={disableFields || packageData.allowProration}
                      {...form.getInputProps('installmentIntervalCount')}
                    />
                    <Select
                      size='xs'
                      data={PERIODOPTIONS}
                      disabled={disableFields || packageData.allowProration}
                      {...form.getInputProps('installmentInterval')}
                    />
                  </Group>

                  <div />
                  <Checkbox
                    label='Equal Division'
                    checked={form.values.isEqualDivision}
                    onChange={handleEqualDivision}
                    disabled={disableFields || packageData.allowProration}
                  />

                  <div />
                  {form.values.isEqualDivision ? (
                    <div className={classes.installmentContainer}>
                      <MoneyField
                        country={session?.gym?.country}
                        size='xs'
                        disabled
                        {...form.getInputProps('installments.0.price')}
                      />
                      <Input.Label my='0.25rem' size='xs'>
                        Per Installment
                      </Input.Label>
                    </div>
                  ) : (
                    <Stack>
                      {form.values.installments.map((installment, index) => (
                        <div className={classes.installmentContainer} key={installment.installmentId}>
                          <MoneyField
                            country={session?.gym?.country}
                            size='xs'
                            disabled={disableFields}
                            value={form.values.installments[index].price}
                            onChange={value => handleInstallmentPriceChange(index, value)}
                          />
                          <Input.Label my='0.25rem' size='xs'>
                            {`Installment #${installment.installmentNumber}`}
                          </Input.Label>
                        </div>
                      ))}
                    </Stack>
                  )}
                  {formState !== 'view' &&
                    packageData.attendanceRules[0].validityInterval !== form.values.installmentInterval && (
                      <KiloAlert variant='filled' variantStyle='warning'>
                        The package length and billing frequency are set in different units. This will
                        negatively impact how revenue and billing are calculated. Please consider using the
                        same unit for both.
                      </KiloAlert>
                    )}
                </>
              )}
            </div>

            {packageData.type !== 'TIME_BASED' && hasMultipleInstallments && (
              <Group py='sm' px='lg'>
                <RenewalBillingAlert packageType={packageData.type} />
              </Group>
            )}

            <SessionValueDisplay
              totalEstimatedSessions={totalEstimatedSessions}
              sessionValue={sessionValue}
              totalPrice={pretaxTotalPrice}
              isEditing={formState === 'edit' || formState === 'create'}
              isTaxInclusive={isTaxInclusive}
            />
          </Collapse>
        </Paper>
      </form>

      {billingPlan && (
        <ModalBillingPlanDelete
          opened={deleteModalOpened}
          handleClose={closeDeleteModal}
          billingPlanId={billingPlan.billingPlanId}
        />
      )}
      <ModalBillingPlanEditConfirmation
        opened={editConfirmationModalOpened}
        handleClose={editConfirmationModalHandlers.close}
        formId={formId}
        updateBillingPlan={updateBillingPlan}
      />
    </>
  );
};
export default BillingPlanForm;
